import React, { useState, useEffect } from "react"
import { Button, Grid, Autocomplete, FormControl, TextField } from "@mui/material";
import { authFetch } from "~/services/fetch";
import { useHookstate } from "@hookstate/core";
import { useLang } from "~/hooks/useLang";
import BodyMarkings from "./BodyMarkings";
import { normilizePositionBodyMarkings } from "~/utils/format";
import { getInjuryTypes } from "~/features/Dispatch/services";
export interface IclickPositions {
  readonly xPosition: number;
  readonly yPosition: number;
  readonly injury_type_id: number;
  readonly acronym?: string;
  readonly name?: string;
}

export interface IInjuryType {
  readonly id: number;
  readonly name: string;
  readonly acronym: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly deleted_at: string | null;
}

export interface injuryMarks {
  readonly front: readonly {
    readonly injury_type_id: string | number;
    readonly xPosition: string;
    readonly yPosition: string;
  }[] | null;
  readonly back: readonly {
    readonly injury_type_id: string | number;
    readonly xPosition: string;
    readonly yPosition: string;
  }[] | null;
}

interface ImageActionFormBuilderProps {
  readonly onValueChange: (value) => void;
  readonly disabled?: boolean
  readonly isRequired?: boolean;
  readonly label: string;
  readonly value: readonly injuryMarks[] | null;
}

// eslint-disable-next-line max-lines-per-function
export default function ImageActionFormBuilder({ onValueChange, disabled, isRequired, label, value }: ImageActionFormBuilderProps) {
  const { translate } = useLang();
  const clickPositionsFront = useHookstate<IclickPositions[]>([]);
  const clickPositionsBack = useHookstate<IclickPositions[]>([]);
  const [currentWoundType, setCurrentWoundType] = useState<IInjuryType | null>(null)
  const [injuryTypes, setInjuryTypes] = useState<readonly IInjuryType[]>([])
  const [selectedInjuryType, setSelectedInjuryType] = useState<IInjuryType | null>(null)

  useEffect(() => {
    const fetchInjuryTypes = async () => {
      try {
        const InjuryTypesResponse: IInjuryType[] = await getInjuryTypes()

        setInjuryTypes(InjuryTypesResponse);

        return true;
      } catch (error) {
        console.error('Erro ao buscar injury types:', error);
      }
    };

    fetchInjuryTypes();
  }, []);

  useEffect(() => {
    if (value && injuryTypes.length > 0) {

      if (value?.['front']) {
        clickPositionsFront.set([]);
        const normalizedFront = normilizePositionBodyMarkings({ value: value['front'], injuryTypes });
        clickPositionsFront.set(normalizedFront);
      }

      if (value?.['back']) {
        clickPositionsBack.set([]);
        const normalizedBack = normilizePositionBodyMarkings({ value: value['back'], injuryTypes });
        clickPositionsBack.set(normalizedBack);
      }
    }
  }, [injuryTypes])

  const updateValeuInput = () => {
    const injuryMarksData = {
      front: clickPositionsFront?.get().map(pos => {
        const injuryType = injuryTypes.find(type => type.id === pos.injury_type_id);
        return {
          injury_type_id: String(injuryType?.id ?? pos.injury_type_id),
          x: String(pos.xPosition),
          y: String(pos.yPosition),
        };
      }),
      back: clickPositionsBack?.get().map(pos => {
        const injuryType = injuryTypes.find(type => type.id === pos.injury_type_id);
        return {
          injury_type_id: String(injuryType?.id ?? pos.injury_type_id),
          x: String(pos.xPosition),
          y: String(pos.yPosition),
        };
      })
    };

    onValueChange(injuryMarksData);
  }

  const handlePress = (event?, front?) => {
    if (!currentWoundType?.id) return null;
    if (currentWoundType?.acronym == '') return;

    const { offsetX, offsetY } = event.nativeEvent;

    if (!offsetX || !offsetY || offsetX < 0 || offsetY < 0) {
      return;
    }

    const normalizedX = offsetX / 500;
    const normalizedY = offsetY / 500;

    if (normalizedX < 0 || normalizedX > 1 || normalizedY < 0 || normalizedY > 1) {
      return;
    }

    const existingPositions = front ? clickPositionsFront.get() : clickPositionsBack.get();
    const minDistance = 0.015;

    const isNearExistingPosition = existingPositions.some(pos => {
      const distance = Math.sqrt(
        Math.pow(pos.xPosition - normalizedX, 2) + Math.pow(pos.yPosition - normalizedY, 2)
      );
      return distance < minDistance;
    });

    if (isNearExistingPosition) {
      return;
    }

    const newPosition: IclickPositions = {
      xPosition: normalizedX,
      yPosition: normalizedY,
      injury_type_id: Number(currentWoundType?.id),
      acronym: currentWoundType?.acronym,
      name: currentWoundType?.name,
    };

    if (front) {
      clickPositionsFront.merge([newPosition]);
      updateValeuInput()
    } else {
      clickPositionsBack.merge([newPosition]);
      updateValeuInput()
    }
  };

  const clearPositionsMarks = ({ side }) => {
    if (side === 'front') {
      clickPositionsFront.set([]);
    } else if (side === 'back') {
      clickPositionsBack.set([]);
    } else {
      clickPositionsFront.set([]);
      clickPositionsBack.set([]);
    }
    updateValeuInput();
  }


  return (
    <>
      <Grid container sx={{ display: 'flex', flexDirection: 'row', width: '100%', marginBottom: 2, gap: 2 }}>
        <Grid item xs={5.5}>
          <FormControl fullWidth size='small'>
            <Autocomplete
              id='filter-injury-type'
              options={injuryTypes}
              value={selectedInjuryType}
              disabled={disabled}
              noOptionsText="Nenhuma opção"
              size='small'
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              getOptionLabel={(option) => option.name && option.acronym ? `[${option.acronym}] ${option.name}` : option.name ?? ''}
              onChange={(event, injuryType) => {
                setSelectedInjuryType(injuryType);
                setCurrentWoundType(injuryType);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required={isRequired}
                  label={label} />
              )}
              clearText="Limpar" />
          </FormControl>
        </Grid>
        <Grid item xs={12} display={'flex'} flexDirection={'row'} justifyContent={'flex-start'} gap={2}>
          <Button
            size="small"
            variant="contained"
            disabled={disabled}
            onClick={() => clearPositionsMarks({ side: 'front' })}>
            {translate('Clear front')}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={disabled}
            onClick={() => clearPositionsMarks({ side: 'back' })}>
            {translate('Clear back')}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={disabled}
            onClick={() => clearPositionsMarks({ side: 'both' })}>
            {translate('Clear both')}
          </Button>
        </Grid>
      </Grid>
      <Grid container xs={12}>
        <BodyMarkings
          clickPositions={clickPositionsFront.get()}
          handlePress={handlePress}
          disabled={disabled}
          side="front"
        />
        <BodyMarkings
          clickPositions={clickPositionsBack.get()}
          handlePress={handlePress}
          disabled={disabled}
          side="back"
        />
      </Grid>
    </>
  )
}